export interface TaxRuleFieldError {
  /** Dotted path from the API, e.g. `brackets.1.min`. */
  field: string
  message: string
}

/**
 * Pulls field-level validation errors out of a failed tax-rule request.
 *
 * The tax-rule API answers 400 with `fieldErrors: [{ field, message }]` for
 * value rules (bounds, contiguous slabs). Request-shape errors come from
 * class-validator as plain strings that start with the property path
 * ("brackets.0.min must be a number ..."), so those are split the same way.
 */
export function getTaxRuleFieldErrors(error: unknown): TaxRuleFieldError[] {
  const data = (error as { response?: { status?: number; data?: unknown } })?.response
  if (data?.status !== 400) return []
  const body = data.data as { fieldErrors?: unknown; message?: unknown } | undefined

  if (Array.isArray(body?.fieldErrors)) {
    return body.fieldErrors.filter(
      (e): e is TaxRuleFieldError =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as TaxRuleFieldError).field === 'string' &&
        typeof (e as TaxRuleFieldError).message === 'string'
    )
  }

  const messages = Array.isArray(body?.message) ? body.message : []
  return messages.flatMap((m) => {
    if (typeof m !== 'string') return []
    const match = m.match(/^([a-z][a-zA-Z]*(?:\.\d+\.[a-zA-Z]+)?)\s+(?:must|should)\b/)
    return match ? [{ field: match[1], message: m }] : []
  })
}

/** 409 from a tax-rule update: a newer version of the rule already exists. */
export const isConflictError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 409
